import BrowserUpdated from '@mui/icons-material/BrowserUpdated';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { action, computed, makeObservable, observable, runInAction, when } from 'mobx';
import { inject, observer } from 'mobx-react';
import ErrorUtil from '@/util/ErrorUtil';
import ReactComponent from '../ReactComponent';
import ModalConfirmacao from './ModalConfirmacao';

// prettier-ignore
export const MODULO = {
    PAUTA:       'PAUTA',
    SALA_SESSAO: 'SALA_SESSAO',
    PRE_SESSAO:  'PRE_SESSAO'
};

class RecarregarCacheServidor extends ReactComponent {
    modalConfirmacaoRecarregamentoCacheOpen = false;
    processando = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            modalConfirmacaoRecarregamentoCacheOpen: observable,
            processando: observable,
            cursor: computed,
            recarregarSemCache: action,
            handleClickRenovarCache: action,
            callbackConfirmarRecarregamentoSemCache: action,
            callbackCancelarRecarregamentoSemCache: action,
            titulo: computed
        });
    }

    get cursor() {
        return this.processando ? 'wait' : 'pointer';
    }

    recarregarSemCache = async () => {
        if (this.processando) {
            return;
        }

        runInAction(() => {
            this.processando = true;
        });

        try {
            if (this.observableProps.modulo === MODULO.SALA_SESSAO) {
                await this.salaSessaoStore.recarregarListaProcessosSemCache();
            } else if (this.observableProps.modulo === MODULO.PAUTA) {
                const promises = [];
                promises.push(this.sessaoStore.recarregarListaProcessosSemCache());
                promises.push(this.pautaJulgamentoStore.carregarProcessosPautaveisSemCache());
                await Promise.all(promises);

                when(
                    () => !this.pautaJulgamentoStore.carregandoProcessos && !this.salaSessaoStore.carregandoProcessos,
                    () => (this.processando = false)
                );
            } else {
                throw new Error(`Módulo ${this.observableProps.modulo} não mapeado.`);
            }
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(
                `Falha ao tentar renovar o cache da pauta/sessão: ${ErrorUtil.montarMensagemErro(error)}`
            );
            console.error(error);
        } finally {
            runInAction(() => {
                this.processando = false;
            });
        }
    };

    handleClickRenovarCache = () => {
        this.modalConfirmacaoRecarregamentoCacheOpen = true;
    };

    callbackConfirmarRecarregamentoSemCache = async () => {
        this.modalConfirmacaoRecarregamentoCacheOpen = false;
        await this.recarregarSemCache();
        this.mensagemStore.abrirMensagemSucesso('Cache renovado e dados recarregados.');
    };

    callbackCancelarRecarregamentoSemCache = () => {
        this.modalConfirmacaoRecarregamentoCacheOpen = false;
    };

    renderModalConfirmacaoRenovacaoCache() {
        return (
            <ModalConfirmacao
                open={this.modalConfirmacaoRecarregamentoCacheOpen}
                callbackConfirmar={this.callbackConfirmarRecarregamentoSemCache}
                callbackCancelar={this.callbackCancelarRecarregamentoSemCache}
                titulo={'Forçar recarregamento de dados'}
            >
                <Stack
                    direction="row"
                    spacing={2}
                >
                    <Box>
                        <WarningIcon sx={{ verticalAlign: 'middle', color: 'darkorange' }} />
                    </Box>
                    <Box>
                        <Typography align={'justify'}>
                            <b>ATENÇÃO</b>: Essa ação provocará uma renovação dos dados de acesso rápido, o que
                            ocasionará lentidão temporária para todos os demais clientes da sessão (usuários dos
                            sistemas Plenário e Secretaria).
                        </Typography>
                        <Typography
                            align={'justify'}
                            sx={{ mt: 2 }}
                        >
                            {`Execute-a apenas caso seja observada inconsistência entre os dados disponíveis ${
                                this.store?.rootStore?.parametroStore?.isOrgaoTST
                                    ? 'nos sistemas de tramitação processual de origem (SIJ e/ou PJe)'
                                    : 'no sistema PJe'
                            } e os dados do sistema Secretaria Eletrônica.`}
                        </Typography>
                        <Typography sx={{ mt: 2 }}>
                            <b>Deseja confirmar o recarregamento dos dados?</b>
                        </Typography>
                    </Box>
                </Stack>
            </ModalConfirmacao>
        );
    }

    get titulo() {
        let t = 'Forçar recarregamento de dados';

        if (this.observableProps.modulo) {
            if (this.observableProps.modulo === MODULO.SALA_SESSAO) {
                t += ' da sessão';
            } else if (this.observableProps.modulo === MODULO.PAUTA) {
                t += ' da pauta/sessão';
            }
        }

        return t;
    }

    render() {
        if (!this.isComponenteHabilitado('CacheServidor')) {
            return null;
        }

        return (
            <Box sx={{ cursor: this.cursor }}>
                <Tooltip title={<Typography>{this.titulo}</Typography>}>
                    <span>
                        <IconButton
                            className="corBotaoAmarelo"
                            onClick={this.handleClickRenovarCache}
                            disabled={this.processando}
                        >
                            <BrowserUpdated sx={{ color: 'white' }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {this.renderModalConfirmacaoRenovacaoCache()}
            </Box>
        );
    }
}

export default inject('stores')(observer(RecarregarCacheServidor));
